import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  IconButton,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Editor from "@monaco-editor/react";
import CloseIcon from "@mui/icons-material/Close";
import { useSystemsContext } from "../../context/SystemsContext";
import type { SystemSection } from "../../hooks/systems/useSystemsState";
import { useSession } from "../../context/SessionContext";

const parseJsonError = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return "Invalid JSON";
};

type ChangeItem = {
  path: string;
  before: unknown;
  after: unknown;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const collectDifferences = (
  before: unknown,
  after: unknown,
  basePath = ""
): ChangeItem[] => {
  if (isPlainObject(before) && isPlainObject(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    const nested = Array.from(keys).flatMap((key) => {
      const nextPath = basePath ? `${basePath}.${key}` : key;
      return collectDifferences(before[key], after[key], nextPath);
    });
    return nested;
  }

  if (Array.isArray(before) && Array.isArray(after)) {
    if (JSON.stringify(before) === JSON.stringify(after)) return [];
    return [{ path: basePath || "(root)", before, after }];
  }

  if (Object.is(before, after)) return [];
  return [{ path: basePath || "(root)", before, after }];
};

const formatChangeValue = (value: unknown): string => {
  if (value === undefined) return "(missing)";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getValueByPath = (obj: any, path: string): unknown => {
  if (!path || path === "(root)") return obj;
  return path.split(".").reduce((acc: any, key) => (acc == null ? undefined : acc[key]), obj);
};

const setValueByPath = (obj: any, path: string, value: unknown): any => {
  if (path === "(root)") return value;
  const keys = path.split(".");
  const root = structuredClone(obj ?? {});
  let cursor: any = root;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    if (!cursor[key] || typeof cursor[key] !== "object" || Array.isArray(cursor[key])) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }
  cursor[keys[keys.length - 1]] = value;
  return root;
};

const deleteValueByPath = (obj: any, path: string): any => {
  if (path === "(root)") return {};
  const keys = path.split(".");
  const root = structuredClone(obj ?? {});
  let cursor: any = root;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    if (!cursor[key] || typeof cursor[key] !== "object") return root;
    cursor = cursor[key];
  }
  delete cursor[keys[keys.length - 1]];
  return root;
};

const isConfigSection = (section: SystemSection) =>
  section === "clientProcess" ||
  section === "assetProcessing" ||
  section === "assetStorage" ||
  section === "assetSyndication";

const getEditorScope = (
  formData: any,
  selectedSection: SystemSection,
  selectedConfig: string | null
) => {
  if (selectedSection === "systemInfo") {
    const { configurations, ...systemInfo } = formData || {};
    return {
      label: "System Info",
      json: systemInfo,
    };
  }

  if (isConfigSection(selectedSection) && selectedConfig) {
    return {
      label: `${selectedConfig}.${selectedSection}`,
      json: formData?.configurations?.[selectedConfig]?.[selectedSection] ?? {},
    };
  }

  return {
    label: "Full System (fallback)",
    json: formData ?? {},
  };
};

const validateSystemShape = (payload: any, selectedSection: SystemSection): string[] => {
  const errors: string[] = [];

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    errors.push("Root JSON must be an object.");
    return errors;
  }

  if (selectedSection === "systemInfo") {
    if (typeof payload.systemCode !== "string" || !payload.systemCode.trim()) {
      errors.push("`systemCode` is required and must be a non-empty string.");
    }

    if (typeof payload.commonName !== "string" || !payload.commonName.trim()) {
      errors.push("`commonName` is required and must be a non-empty string.");
    }

    return errors;
  }

  if (selectedSection === "clientProcess") {
    if (typeof payload.name !== "string" || !payload.name.trim()) {
      errors.push("`name` is required and must be a non-empty string.");
    }
  }

  return errors;
};

const SystemJsonEditor: React.FC = () => {
  const {
    formData,
    saveRawSystem,
    selectedSection,
    selectedConfig,
    setFormData,
    setDirty,
  } = useSystemsContext();
  const { selectedSystem } = useSession();
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isDark = theme.palette.mode === "dark";

  const [jsonText, setJsonText] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [scopeLabel, setScopeLabel] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSystem, setPendingSystem] = useState<any | null>(null);
  const [pendingChanges, setPendingChanges] = useState<ChangeItem[]>([]);

  useEffect(() => {
    const scope = getEditorScope(formData, selectedSection, selectedConfig);
    setScopeLabel(scope.label);
    setJsonText(JSON.stringify(scope.json, null, 2));
    setErrors([]);
    setSuccessMessage("");
  }, [formData, selectedSection, selectedConfig]);

  const handleValidate = () => {
    setSuccessMessage("");
    try {
      const parsed = JSON.parse(jsonText);
      const shapeErrors = validateSystemShape(parsed, selectedSection);
      if (shapeErrors.length) {
        setErrors(shapeErrors);
        return false;
      }
      setErrors([]);
      setSuccessMessage("JSON is valid.");
      return true;
    } catch (error) {
      setErrors([parseJsonError(error)]);
      return false;
    }
  };

  const handleSave = async () => {
    setSuccessMessage("");
    if (!handleValidate()) return;

    try {
      const parsed = JSON.parse(jsonText);
      let nextSystem = structuredClone(formData || {});

      if (selectedSection === "systemInfo") {
        nextSystem = {
          ...nextSystem,
          ...parsed,
          configurations: nextSystem.configurations || {},
        };
      } else if (isConfigSection(selectedSection) && selectedConfig) {
        if (!nextSystem.configurations?.[selectedConfig]) {
          throw new Error(`Configuration "${selectedConfig}" was not found.`);
        }

        if (
          selectedSection === "clientProcess" &&
          typeof parsed?.name === "string" &&
          parsed.name.trim() !== selectedConfig
        ) {
          throw new Error(
            "Renaming configuration keys is not supported in scoped JSON mode. Keep `clientProcess.name` equal to the selected configuration name."
          );
        }

        nextSystem.configurations[selectedConfig][selectedSection] = parsed;
      } else {
        nextSystem = parsed;
      }

      const baseSystem = selectedSystem || {};
      const diffItems = collectDifferences(baseSystem, nextSystem);
      if (diffItems.length === 0) {
        setSuccessMessage("No changes detected.");
        return;
      }

      setPendingSystem(nextSystem);
      setPendingChanges(diffItems);
      setConfirmOpen(true);
    } catch (error) {
      setErrors([parseJsonError(error)]);
    }
  };

  const handleConfirmSave = async () => {
    if (!pendingSystem) return;
    setIsSaving(true);
    setSuccessMessage("");
    try {
      await saveRawSystem(pendingSystem);
      setConfirmOpen(false);
      setPendingSystem(null);
      setPendingChanges([]);
      setSuccessMessage("Saved system JSON to AWS.");
    } catch (error) {
      setErrors([parseJsonError(error)]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearAllChanges = () => {
    if (!selectedSystem) return;
    setFormData(structuredClone(selectedSystem));
    setDirty(false);
    setErrors([]);
    setSuccessMessage("Cleared all unsaved changes.");
    setConfirmOpen(false);
    setPendingSystem(null);
    setPendingChanges([]);
  };

  const handleRevertSingleChange = (path: string) => {
    if (!pendingSystem) return;
    const baseSystem = selectedSystem || {};

    const baseValue = getValueByPath(baseSystem, path);
    const nextPending =
      baseValue === undefined
        ? deleteValueByPath(pendingSystem, path)
        : setValueByPath(pendingSystem, path, baseValue);

    const nextDiff = collectDifferences(baseSystem, nextPending);
    setPendingSystem(nextPending);
    setPendingChanges(nextDiff);
    setFormData(nextPending);
    setDirty(nextDiff.length > 0);

    if (nextDiff.length === 0) {
      setConfirmOpen(false);
      setSuccessMessage("No changes remaining to save.");
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        System JSON Editor (Admin)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Editing: {scopeLabel}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleValidate}
          sx={{
            borderColor: alpha(accent, isDark ? 0.75 : 0.85),
            color: accent,
            "&:hover": {
              borderColor: accent,
              backgroundColor: alpha(accent, 0.1),
            },
          }}
        >
          Validate JSON
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            backgroundColor: accent,
            color: "#fff",
            "&:hover": { backgroundColor: accent, opacity: 0.92 },
            "&.Mui-disabled": {
              backgroundColor: alpha(accent, 0.45),
              color: alpha("#fff", 0.8),
            },
          }}
        >
          {isSaving ? "Saving..." : "Validate and Save"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleClearAllChanges}
          disabled={!selectedSystem || isSaving}
          sx={{
            borderColor: alpha(accent, isDark ? 0.55 : 0.65),
            color: theme.palette.text.secondary,
            "&:hover": {
              borderColor: alpha(accent, 0.8),
              backgroundColor: alpha(accent, 0.06),
            },
          }}
        >
          Clear Changes
        </Button>
      </Stack>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.join(" ")}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Box>
        <Box
          sx={{
            border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.28 : 0.24)}`,
            borderRadius: 1,
            overflow: "hidden",
            "&:hover": {
              borderColor: alpha(accent, 0.8),
            },
          }}
        >
          <Editor
            height="65vh"
            defaultLanguage="json"
            language="json"
            theme={isDark ? "vs-dark" : "vs"}
            value={jsonText}
            onChange={(value) => setJsonText(value ?? "")}
            options={{
              minimap: { enabled: false },
              folding: true,
              foldingStrategy: "auto",
              lineNumbers: "on",
              lineNumbersMinChars: 4,
              scrollBeyondLastLine: false,
              wordWrap: "on",
              tabSize: 2,
              insertSpaces: true,
              automaticLayout: true,
              fontSize: 13,
              fontFamily: "Menlo, Monaco, Consolas, 'Courier New', monospace",
              renderWhitespace: "selection",
              bracketPairColorization: { enabled: true },
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (isSaving) return;
          setConfirmOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Confirm JSON Changes</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
            Review changes before saving to AWS.
          </Typography>
          <List dense sx={{ maxHeight: "50vh", overflow: "auto" }}>
            {pendingChanges.map((change, index) => (
              <ListItem
                key={`${change.path}-${index}`}
                alignItems="flex-start"
                secondaryAction={
                  change.path !== "(root)" ? (
                    <Tooltip title="Revert this change">
                      <IconButton
                        edge="end"
                        aria-label={`Revert ${change.path}`}
                        onClick={() => handleRevertSingleChange(change.path)}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : undefined
                }
              >
                <ListItemText
                  primary={change.path}
                  secondary={`From: ${formatChangeValue(change.before)}  ->  To: ${formatChangeValue(change.after)}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (isSaving) return;
              setConfirmOpen(false);
            }}
            variant="outlined"
            sx={{
              borderColor: alpha(accent, isDark ? 0.75 : 0.85),
              color: accent,
              "&:hover": {
                borderColor: accent,
                backgroundColor: alpha(accent, 0.1),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            disabled={isSaving}
            variant="contained"
            sx={{
              backgroundColor: accent,
              color: "#fff",
              "&:hover": { backgroundColor: accent, opacity: 0.92 },
              "&.Mui-disabled": {
                backgroundColor: alpha(accent, 0.45),
                color: alpha("#fff", 0.8),
              },
            }}
          >
            {isSaving ? "Saving..." : "Confirm and Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SystemJsonEditor;
